import { useCallback, useRef } from "react";
import { parseAndValidatePortalSchema } from "../../../shared/schema";

interface ImportResult {
  success: boolean;
  errors?: string[];
}

export function useSchemaImport(onImport: (json: unknown) => ImportResult) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const openFilePicker = useCallback(() => {
    // Create hidden file input if it doesn't exist
    if (!inputRef.current) {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".json,application/json";
      input.style.display = "none";

      input.addEventListener("change", async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) return;

        try {
          const text = await file.text();
          const result = parseAndValidatePortalSchema(text);

          if (result.success && result.data) {
            const importResult = onImport(result.data);
            if (!importResult.success) {
              console.error("Import failed:", importResult.errors);
            }
          } else {
            console.error("Validation failed:", result.errors);
          }
        } catch (err) {
          console.error("Failed to read file:", err);
        }

        // Reset input for next selection
        input.value = "";
      });

      document.body.appendChild(input);
      inputRef.current = input;
    }

    inputRef.current.click();
  }, [onImport]);

  // Cleanup on unmount
  const cleanup = useCallback(() => {
    if (inputRef.current) {
      document.body.removeChild(inputRef.current);
      inputRef.current = null;
    }
  }, []);

  return { openFilePicker, cleanup };
}
